import { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import MainLayout from "Layouts/MainLayout/MainLayout";
import Table from "Components/Common/Table/Table";
import { Button } from "Components/Common/Button/Button";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import ClassNames from "Helpers/Common";
import "Colors.css";
import { useCampaign, useCampaignRecipients, useStartCampaign, useRunCampaignNow } from "CustomHooks/useEmailMarketing";
import { Badge } from "./EmailsPage";

const PER_PAGE = 25;
const TABS = ["Recipients", "Overview"];
const TH = "px-4 py-3 text-left text-xs font-medium light_grayish_navy uppercase tracking-wider";
const TD = "px-4 py-3 text-sm text-black";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const RecipientStatusChip = ({ r }) => {
  const s = r.deliveryStatus || r.status; // post-send delivery outcome wins over send status
  const color =
    s === "delivered" ? "text-green-700"
      : ["bounce", "blocked", "dropped", "spamreport", "failed"].includes(s) ? "text-red-700"
        : s === "sent" ? "text-blue-700"
          : "light_grayish_navy";
  return <span className={ClassNames("text-sm capitalize", color)}>{s}</span>;
};

const RecipientsTab = ({ id }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCampaignRecipients(id, page, PER_PAGE);
  const rows = data?.recipients?.data || [];
  const total = data?.recipients?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <>
      <Table
        heading={
          <>
            <th className={TH}>Recipient</th>
            <th className={TH}>Status / Delivery</th>
            <th className={TH}>Opened</th>
            <th className={TH}>Clicked</th>
            <th className={TH}>Sent</th>
            <th className={TH}>Error</th>
          </>
        }
        body={
          isLoading ? null : rows.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-6 text-center light_grayish_navy">No recipients yet — start the campaign to snapshot the audience.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r._id} className="border-t border-gray-100">
                <td className={TD}>
                  <div className="font-medium">{r.email}</div>
                  {r.name ? <div className="text-xs light_grayish_navy">{r.name}</div> : null}
                </td>
                <td className={TD}><RecipientStatusChip r={r} /></td>
                <td className={ClassNames(TD, r.openedAt ? "text-green-700" : "light_grayish_navy")}>{r.openedAt ? fmt(r.openedAt) : "—"}</td>
                <td className={ClassNames(TD, "light_grayish_navy")}>{r.clickedAt ? fmt(r.clickedAt) : "—"}</td>
                <td className={ClassNames(TD, "light_grayish_navy")}>{fmt(r.sentAt)}</td>
                <td className={ClassNames(TD, "text-red-700 text-xs")}>{r.error || ""}</td>
              </tr>
            ))
          )
        }
      />
      {isLoading && <Spinner />}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm light_grayish_navy">Page {Math.min(page, totalPages)} of {totalPages} · {total} recipients</div>
          <div className="w-56">
            <PaginationBtns
              onPrevious={() => page > 1 && setPage(page - 1)}
              onNext={() => page < totalPages && setPage(page + 1)}
              isPrevDisabled={page <= 1}
              isNextDisabled={page >= totalPages}
            />
          </div>
        </div>
      )}
    </>
  );
};

const OverviewTab = ({ campaign }) => {
  if (!campaign) return null;
  const Item = ({ label, value }) => (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs light_grayish_navy uppercase tracking-wider">{label}</div>
      <div className="text-lg font-semibold text-black mt-1">{value}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Item label="Total" value={campaign.totalRecipients ?? 0} />
      <Item label="Accepted by SendGrid" value={campaign.sentCount ?? 0} />
      <Item label="Delivered" value={<span className="text-green-700">{campaign.deliveredCount ?? 0}</span>} />
      <Item label="Audience window" value={`${campaign.audienceWindowDays}d`} />
      <Item label="Template" value={<span className="text-sm break-all">{campaign.templateId}</span>} />
      <Item label="Subject" value={<span className="text-sm">{campaign.subject || "—"}</span>} />
      <Item label="Started" value={<span className="text-sm">{fmt(campaign.startedAt)}</span>} />
      <Item label="Completed" value={<span className="text-sm">{fmt(campaign.completedAt)}</span>} />
    </div>
  );
};

export const EmailCampaignDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const [tab, setTab] = useState("Recipients");
  const { data: campaign, refetch: refetchCampaign } = useCampaign(id);
  const start = useStartCampaign();
  const runNow = useRunCampaignNow();

  return (
    <MainLayout>
      <div className="p-8 flex flex-col gap-6">
        {/* Patient-flow style header: back arrow + title + tab pills, actions on the right */}
        <div className="flex justify-between items-center gap-3">
          <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-6 gap-y-2 mr-4">
            <button type="button" onClick={() => history.push("/emails")} className="flex items-center mr-2 focus:outline-none" title="Back to Emails">
              <span className="sapphire text-xl leading-none">←</span>
              <h1 className="ml-2 font-semibold text-2xl sapphire cursor-pointer">{campaign?.name || "Campaign"}</h1>
            </button>
            {campaign && <Badge status={campaign.status} />}
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { setTab(t); refetchCampaign(); }}
                className={ClassNames(
                  "text-sm font-medium cursor-pointer select-none text-center py-2 px-2 rounded-md focus:outline-none whitespace-nowrap",
                  tab === t ? "bg-gray-100 font-semibold text-black" : "light_grayish_navy hover:bg-gray-50"
                )}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {campaign?.status === "draft" && (
              <Button btnText="Start" onClick={() => start.mutate(id)} isLoading={start.isLoading} className="py-2 px-4" />
            )}
            {campaign?.status === "running" && (
              <Button btnText="Send now" variant="Secondary" onClick={() => runNow.mutate(id)} isLoading={runNow.isLoading} className="py-2 px-4" />
            )}
          </div>
        </div>
        <div className="text-xs light_grayish_navy -mt-3">{campaign?.templateId} · {campaign?.audienceWindowDays}d window</div>

        {tab === "Recipients" ? <RecipientsTab id={id} /> : <OverviewTab campaign={campaign} />}
      </div>
    </MainLayout>
  );
};

export default EmailCampaignDetail;
